import { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, TrendingDown, Minus, Calendar, Building2, AlertTriangle, ShieldAlert, Zap, Map } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, Cell, LabelList } from 'recharts';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import PortfolioImpactPanel from '../components/PortfolioImpactPanel.jsx';
import TrackRecordPanel from '../components/TrackRecordPanel.jsx';
import { usePage } from '../contexts/PageContext.jsx';
import { usePageTracking } from '../hooks/usePageTracking.js';

const STRENGTH_COLORS = { strengthening: '#10b981', weakening: '#ef4444', stable: '#6b7280' };
const TAIL_COLORS = { calm: '#10b981', watching: '#f59e0b', elevated: '#f97316', crisis: '#ef4444' };
const TENOR_ORDER = { '3M': 0, '2Y': 1, '5Y': 2, '10Y': 3, '30Y': 4 };

function StrengthTile({ currency, score, trend }) {
  const color = STRENGTH_COLORS[trend] || '#6b7280';
  const Icon = trend === 'strengthening' ? TrendingUp : trend === 'weakening' ? TrendingDown : Minus;
  return (
    <div className="bg-korinth-bg rounded-lg border border-korinth-border/50 px-2 py-2 text-center" style={{ borderLeftColor: color, borderLeftWidth: 3 }}>
      <div className="text-sm font-mono font-bold text-white">{currency}</div>
      <div className="text-xl font-mono font-bold" style={{ color }}>{score?.toFixed(0) || '—'}</div>
      <Icon className="w-3 h-3 mx-auto" style={{ color }} />
    </div>
  );
}

export default function CurrencyIntelligence() {
  const { setPage } = usePage();
  useEffect(() => { setPage('currency'); }, []);

  const [strengths, setStrengths] = useState([]);
  const [pairs, setPairs] = useState([]);
  const [observations, setObservations] = useState([]);
  const [banks, setBanks] = useState([]);
  const [kdx, setKdx] = useState(null);
  const [kdxHistory, setKdxHistory] = useState([]);
  const [curves, setCurves] = useState([]);
  const [inflation, setInflation] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [sovereign, setSovereign] = useState([]);
  const [currRecs, setCurrRecs] = useState([]);
  const [currPreds, setCurrPreds] = useState([]);
  const [tailRisk, setTailRisk] = useState(null);
  const [cryptoMon, setCryptoMon] = useState([]);
  const [aedExposure, setAedExposure] = useState(null);
  const [fxRegime, setFxRegime] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [simResult, setSimResult] = useState(null);
  const [simLoading, setSimLoading] = useState(false);

  useEffect(() => {
    async function load() {
      const json = async (url) => { try { const r = await fetch(url); return r.ok ? await r.json() : null; } catch { return null; } };
      setStrengths(await json('/api/currency/strength') || []);
      setPairs(await json('/api/currency/pairs') || []);
      setObservations(await json('/api/currency/observations') || []);
      setBanks(await json('/api/sovereign/central-banks') || []);
      setKdx(await json('/api/sovereign/kdx/current'));
      setKdxHistory(await json('/api/sovereign/kdx?days=30') || []);
      setCurves(await json('/api/sovereign/yield-curves') || []);
      setInflation(await json('/api/sovereign/inflation') || []);
      setCalendar(await json('/api/sovereign/calendar') || []);
      setSovereign(await json('/api/sovereign/overview') || []);
      setTailRisk(await json('/api/currency/tail-risk'));
      setCryptoMon(await json('/api/currency/crypto-monetary') || []);
      setAedExposure(await json('/api/currency/aed-exposure'));
      setFxRegime(await json('/api/currency/fx-regime'));
      setScenarios(await json('/api/currency/scenarios') || []);
      // Currency recs/preds
      const ccys = ['EUR', 'GBP', 'JPY', 'CHF', 'AUD', 'CAD', 'CNY'];
      const allRecs = (await json('/api/kairos/recommendations?status=active'))?.recommendations || [];
      const allPreds = (await json('/api/kairos/predictions?status=active'))?.predictions || [];
      setCurrRecs(allRecs.filter(r => ccys.includes(r.asset)));
      setCurrPreds(allPreds.filter(p => ccys.includes(p.asset)));
    }
    load();
    const interval = setInterval(load, 120000);
    return () => clearInterval(interval);
  }, []);

  const sortedCurves = curves.filter(c => c.country === 'US').sort((a, b) => (TENOR_ORDER[a.tenor] || 99) - (TENOR_ORDER[b.tenor] || 99));
  const countries = ['US', 'DE', 'UK', 'JP'];

  async function runScenario(scenarioId) {
    setSimLoading(true);
    try {
      const r = await fetch('/api/currency/scenario/simulate', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ scenarioId }) });
      if (r.ok) setSimResult(await r.json());
    } catch {} finally { setSimLoading(false); }
  }

  const REGIME_COLORS = { DOLLAR_STRENGTH: '#3b82f6', FLIGHT_TO_SAFETY: '#f59e0b', RISK_ON: '#10b981', DEBASEMENT: '#ef4444', POLICY_DIVERGENCE: '#a855f7', RANGING: '#6b7280' };

  const { impact: fxImpact, track: fxTrack, loading: fxLoading } = usePageTracking('currency');

  const divergenceCount = (observations || []).filter(o => o.divergence).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <DollarSign className="w-6 h-6 text-korinth-accent" />
        <h1 className="text-2xl font-bold text-white">Currency Intelligence</h1>
        <span className="text-korinth-muted text-sm font-mono">ATLAS · Sovereign & Fiscal — monetary fundamentals, positioning</span>
      </div>

      {/* Sprint 20: Summary strip */}
      <div className="bg-korinth-bg/50 border border-korinth-border/30 rounded-lg px-4 py-2.5 text-[11px] font-mono text-korinth-text flex flex-wrap items-center gap-4">
        <span><span className="text-korinth-muted">KDX:</span> <span className="text-white font-bold">{kdx?.kdx_score?.toFixed(1) || '—'}</span></span>
        <span><span className="text-korinth-muted">Active FX recs:</span> <span className="text-korinth-accent font-bold">{currRecs?.length || 0}</span></span>
        <span><span className="text-korinth-muted">Divergences:</span> <span className="text-korinth-warning font-bold">{divergenceCount}</span></span>
      </div>

      {/* Sprint 20: Portfolio Impact */}
      <PortfolioImpactPanel topic="Currency" {...fxImpact} loading={fxLoading} />

      {/* 0. Tail Risk Banner */}
      {tailRisk && tailRisk.score > 25 && (
        <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border`} style={{ backgroundColor: TAIL_COLORS[tailRisk.stressLevel] + '15', borderColor: TAIL_COLORS[tailRisk.stressLevel] + '40' }}>
          <ShieldAlert className="w-5 h-5" style={{ color: TAIL_COLORS[tailRisk.stressLevel] }} />
          <div>
            <span className="text-sm font-mono font-bold" style={{ color: TAIL_COLORS[tailRisk.stressLevel] }}>TAIL RISK: {tailRisk.stressLevel.toUpperCase()} ({tailRisk.score}/100)</span>
            <p className="text-[10px] text-korinth-muted">{tailRisk.signals?.slice(0, 2).join(' | ')}</p>
          </div>
        </div>
      )}

      {/* 0.5 FX Regime + Scenario Simulator */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ModuleContainer title="FX Regime" subtitle="Deterministic classification (30-min refresh)">
          {fxRegime ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5" style={{ color: REGIME_COLORS[fxRegime.regime] || '#6b7280' }} />
                <div>
                  <span className="text-lg font-mono font-bold" style={{ color: REGIME_COLORS[fxRegime.regime] || '#6b7280' }}>{fxRegime.regime?.replace(/_/g, ' ')}</span>
                  <span className="ml-2 text-xs text-korinth-muted">({fxRegime.confidence}% confidence)</span>
                </div>
              </div>
              <div className="space-y-1">
                {(fxRegime.signals || []).map((s, i) => (
                  <div key={i} className="text-[10px] font-mono text-korinth-text bg-korinth-bg rounded px-2 py-1">• {s}</div>
                ))}
              </div>
              {fxRegime.detectedAt && <p className="text-[8px] text-korinth-muted font-mono">Last classified: {fxRegime.detectedAt?.slice(11, 19)} UTC</p>}
            </div>
          ) : <p className="text-[10px] text-korinth-muted font-mono text-center py-4">Calculating regime...</p>}
        </ModuleContainer>

        <ModuleContainer title="Scenario Simulator" subtitle="Impact on shadow portfolio + KDX">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-1.5">
              {scenarios.map(s => (
                <button key={s.id} onClick={() => runScenario(s.id)} disabled={simLoading}
                  className="text-[10px] font-mono px-2 py-1.5 rounded bg-korinth-bg border border-korinth-border/50 text-korinth-text hover:border-korinth-accent/50 hover:text-white transition-colors disabled:opacity-50">
                  {s.name}
                </button>
              ))}
            </div>
            {simResult && (
              <div className="bg-korinth-bg rounded p-3 border border-korinth-border/30 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono font-bold text-white">{simResult.scenario}</span>
                  <span className={`text-sm font-mono font-bold ${simResult.totalPnlUsd >= 0 ? 'text-korinth-accent' : 'text-korinth-danger'}`}>
                    {simResult.totalPnlUsd >= 0 ? '+' : ''}{simResult.totalPnlUsd?.toLocaleString()} USD
                  </span>
                </div>
                <div className="flex gap-4 text-[9px] font-mono text-korinth-muted">
                  <span>Portfolio: {simResult.totalPnlPct}%</span>
                  <span>KDX: {simResult.kdxImpact > 0 ? '+' : ''}{simResult.kdxImpact}%</span>
                  <span>{simResult.positionsAffected}/{simResult.positionsTotal} affected</span>
                </div>
                {simResult.positionImpacts?.slice(0, 4).map((p, i) => (
                  <div key={i} className="flex items-center justify-between text-[9px] font-mono">
                    <span className="text-korinth-text">{p.asset} ({p.direction})</span>
                    <span className={p.impactUsd >= 0 ? 'text-korinth-accent' : 'text-korinth-danger'}>{p.impactUsd >= 0 ? '+' : ''}{p.impactUsd?.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </ModuleContainer>
      </div>

      {/* 1. KDX */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ModuleContainer title="KORINTH Dollar Index (KDX)" subtitle="EUR 60% + GBP 20% + CNY 20%">
          <div className="text-center mb-2">
            <div className="text-4xl font-mono font-bold text-white">{kdx?.kdx_value?.toFixed(1) || '—'}</div>
            <div className={`text-sm font-mono ${(kdx?.vs_baseline_pct || 0) >= 0 ? 'text-korinth-accent' : 'text-korinth-danger'}`}>
              {kdx?.vs_baseline_pct != null ? `${kdx.vs_baseline_pct >= 0 ? '+' : ''}${kdx.vs_baseline_pct.toFixed(2)}% vs 30d` : '—'}
            </div>
          </div>
          {kdxHistory.length > 2 ? (
            <div className="h-24">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={kdxHistory.map(h => ({ t: h.calculated_at?.slice(5, 10), v: h.kdx_value }))}>
                  <XAxis dataKey="t" tick={{ fontSize: 8, fill: '#6b7280' }} interval="preserveStartEnd" />
                  <Line type="monotone" dataKey="v" stroke="#10b981" dot={false} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : <p className="text-[9px] text-korinth-muted font-mono text-center">Building 30-day baseline ({kdxHistory.length}/30 days)</p>}
        </ModuleContainer>

        <div className="lg:col-span-2">
          <ModuleContainer title="Currency Strength">
            <div className="grid grid-cols-5 gap-2">
              {strengths.map(s => <StrengthTile key={s.currency} currency={s.currency} score={s.strength_score} trend={s.trend} />)}
            </div>
          </ModuleContainer>
        </div>
      </div>

      {/* 2. KAIROS Currency Calls */}
      {(currRecs.length > 0 || currPreds.length > 0) && (
        <ModuleContainer title="KAIROS Currency Calls" subtitle="Active currency recommendations and predictions">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {currRecs.map(r => (
              <div key={r.id} className={`px-3 py-2 rounded border-l-3 ${r.direction === 'buy' ? 'border-l-korinth-accent bg-korinth-accent/5' : 'border-l-korinth-danger bg-korinth-danger/5'}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono font-bold text-white">{r.asset} <span className={r.direction === 'buy' ? 'text-korinth-accent' : 'text-korinth-danger'}>{r.direction?.toUpperCase()}</span></span>
                  <span className="text-xs font-mono text-korinth-accent">{r.conviction_overall?.toFixed(1)}/10</span>
                </div>
              </div>
            ))}
            {currPreds.map(p => (
              <div key={p.id} className="px-3 py-2 rounded bg-korinth-bg border border-korinth-border/30">
                <span className={`text-xs ${p.direction === 'bullish' ? 'text-korinth-accent' : 'text-korinth-danger'}`}>{p.direction === 'bullish' ? '▲' : '▼'} </span>
                <span className="text-xs font-mono font-bold text-white">{p.asset}</span>
                <span className="text-[9px] font-mono px-1 py-0.5 rounded bg-korinth-border text-korinth-muted ml-1">{p.timeframe}</span>
                <span className="text-[10px] font-mono text-korinth-text ml-2">{p.confidence}%</span>
              </div>
            ))}
          </div>
        </ModuleContainer>
      )}

      {/* 3. Central Bank Radar */}
      <ModuleContainer title="Central Bank Radar" subtitle="Policy rates, guidance, next meetings">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {banks.map(b => (
            <div key={b.bank} className="bg-korinth-bg rounded px-3 py-2 border border-korinth-border/30">
              <div className="flex items-center gap-1 mb-1">
                <Building2 className="w-3 h-3 text-korinth-muted" />
                <span className="text-xs font-mono font-bold text-white">{b.bank}</span>
              </div>
              <div className="text-lg font-mono font-bold text-korinth-text">{b.policy_rate}%</div>
              <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${b.forward_guidance === 'hawkish' ? 'bg-red-500/10 text-red-400' : b.forward_guidance === 'dovish' ? 'bg-green-500/10 text-green-400' : 'bg-korinth-border text-korinth-muted'}`}>{b.forward_guidance}</span>
              <div className="text-[8px] text-korinth-muted mt-1">Next: {b.next_meeting_date?.slice(5) || '—'}</div>
            </div>
          ))}
        </div>
      </ModuleContainer>

      {/* 4. Sovereign Risk (multi-country) */}
      <ModuleContainer title="Sovereign Risk" subtitle="US, Germany, UK, Japan">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {countries.map(c => {
            const debtGdp = sovereign.find(s => s.country === c && s.metric === 'debt_gdp');
            const yield10 = sovereign.find(s => s.country === c && s.metric === '10y_yield');
            const riskColor = (debtGdp?.value || 0) > 150 ? 'text-korinth-danger' : (debtGdp?.value || 0) > 90 ? 'text-korinth-warning' : 'text-korinth-accent';
            return (
              <div key={c} className="bg-korinth-bg rounded px-3 py-2 border border-korinth-border/30 text-center">
                <div className="text-sm font-mono font-bold text-white">{c}</div>
                <div className={`text-xl font-mono font-bold ${riskColor}`}>{debtGdp?.value?.toFixed(0) || '—'}%</div>
                <div className="text-[9px] text-korinth-muted">debt/GDP</div>
                {yield10 && <div className="text-[10px] font-mono text-korinth-text mt-1">10Y: {yield10.value?.toFixed(2)}%</div>}
              </div>
            );
          })}
        </div>
      </ModuleContainer>

      {/* 5. Inflation + Yield Curve */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ModuleContainer title="Real Rates & Inflation">
          <div className="space-y-1.5">
            {inflation.map((i, idx) => (
              <div key={idx} className="flex items-center justify-between px-2 py-1.5 bg-korinth-bg rounded text-[10px] font-mono">
                <span className="text-korinth-muted">{i.metric}</span>
                <span className={`font-bold ${i.metric.includes('real_yield') && i.value < 0 ? 'text-korinth-danger' : 'text-korinth-text'}`}>{i.value?.toFixed(2)}</span>
              </div>
            ))}
            {inflation.length === 0 && <p className="text-[10px] text-korinth-muted font-mono text-center py-4">Requires FRED_API_KEY</p>}
          </div>
        </ModuleContainer>

        <ModuleContainer title="US Yield Curve">
          {sortedCurves.length > 0 ? (
            <div className="h-36">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sortedCurves.map(c => ({ tenor: c.tenor, yield: c.yield_value }))}>
                  <XAxis dataKey="tenor" tick={{ fontSize: 10, fill: '#6b7280' }} />
                  <YAxis tick={{ fontSize: 9, fill: '#6b7280' }} domain={[0, d => Math.ceil(d + 1)]} />
                  <Tooltip contentStyle={{ background: '#111827', border: '1px solid #1f2937', fontSize: 11 }} cursor={false} />
                  <Bar dataKey="yield" fill="#3b82f6" radius={[3, 3, 0, 0]}>
                    <LabelList dataKey="yield" position="top" formatter={v => `${v.toFixed(2)}%`} style={{ fontSize: 9, fill: '#e5e7eb' }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <p className="text-[10px] text-korinth-muted font-mono text-center py-4">Requires FRED_API_KEY</p>}
        </ModuleContainer>
      </div>

      {/* 6. Crypto Monetary + Economic Calendar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ModuleContainer title="Crypto Monetary System" subtitle="BTC dominance, stablecoin supply, BTC/gold ratio">
          <div className="space-y-2">
            {cryptoMon.map(m => (
              <div key={m.metric} className="flex items-center justify-between px-2 py-1.5 bg-korinth-bg rounded text-xs font-mono">
                <span className="text-korinth-muted">{m.metric.replace(/_/g, ' ')}</span>
                <span className="text-white font-bold">{m.metric === 'stablecoin_supply_usd' ? `$${(m.value / 1e9).toFixed(1)}B` : m.metric === 'btc_dominance' ? `${m.value?.toFixed(1)}%` : m.value?.toFixed(2) || '—'}</span>
              </div>
            ))}
            {cryptoMon.length === 0 && <p className="text-[10px] text-korinth-muted font-mono text-center py-4">Loading from CoinGecko...</p>}
          </div>
        </ModuleContainer>

        <ModuleContainer title="Economic Calendar" subtitle="Upcoming events">
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {calendar.length > 0 ? calendar.map((e, i) => (
              <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded text-xs font-mono ${e.importance === 'high' ? 'bg-korinth-danger/5 border border-korinth-danger/20' : 'bg-korinth-bg'}`}>
                <Calendar className={`w-3 h-3 ${e.importance === 'high' ? 'text-korinth-danger' : 'text-korinth-muted'}`} />
                <span className="text-korinth-muted w-12">{e.event_date?.slice(5, 10)}</span>
                <span className="text-white flex-1">{e.event_name}</span>
              </div>
            )) : <p className="text-[10px] text-korinth-muted font-mono text-center py-4">No upcoming events</p>}
          </div>
        </ModuleContainer>
      </div>

      {/* 7. AED Dual View (redesigned) */}
      <ModuleContainer title="AED Effective Exposure" subtitle="Your AED = USD (pegged at 3.6725)">
        {aedExposure ? (
          <div className="space-y-3">
            <div className="bg-blue-500/10 border border-blue-500/20 rounded px-3 py-2 text-xs font-mono text-blue-400">
              Currency risk comes from non-USD exposures. AED tracks USD exactly.
            </div>
            <div className="grid grid-cols-5 gap-2">
              {Object.entries(aedExposure.exposure || {}).map(([bucket, data]) => (
                <div key={bucket} className="bg-korinth-bg rounded px-2 py-2 text-center">
                  <div className="text-sm font-mono font-bold text-white">{data.pct}%</div>
                  <div className="text-[8px] text-korinth-muted uppercase">{bucket}</div>
                  <div className="text-[8px] text-korinth-muted">${(Math.abs(data.notional) / 1000).toFixed(0)}K</div>
                </div>
              ))}
            </div>
          </div>
        ) : <p className="text-[10px] text-korinth-muted font-mono text-center py-4">Loading exposure data...</p>}
      </ModuleContainer>

      {/* 8. Major Pairs */}
      <ModuleContainer title="Major Pairs">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {pairs.map(p => (
            <div key={p.pair} className="flex items-center justify-between px-3 py-2 bg-korinth-bg rounded text-xs font-mono">
              <span className="text-white font-bold">{p.pair}</span>
              <span className="text-korinth-text">{p.latest_price?.toFixed(4)}</span>
              <span className={`${(p.daily_change_pct || 0) >= 0 ? 'text-korinth-accent' : 'text-korinth-danger'}`}>
                {p.daily_change_pct != null ? `${p.daily_change_pct >= 0 ? '+' : ''}${p.daily_change_pct.toFixed(2)}%` : '—'}
              </span>
            </div>
          ))}
        </div>
      </ModuleContainer>

      {/* Sprint 20: Track record */}
      <TrackRecordPanel {...fxTrack} loading={fxLoading} metric_name="Currency / FX" />
    </div>
  );
}
